import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import Badge, { BadgeType } from 'src/components/ui/Badge/Badge';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import ConfirmationModal from 'src/components/modals/ConfirmationModal/ConfirmationModal';
import Divider from 'src/components/ui/Divider/Divider';
import { SectionHeadline } from './InboundConfiguratorForm';
import { InboundOrder, scheduleInboundRefetch, useInboundBackorderCancelMutation } from '../../services/inbound';

// Fallback colour per backorder move state when the API supplies no explicit
// colour (e.g. no quick-filter colour record for "cancel"). The label always
// comes from the API.
const STATUS_BADGE: Record<string, BadgeType> = {
  open: BadgeType.Neutral2,
  done: BadgeType.Successful,
  cancel: BadgeType.Error,
};

const InboundBackordersCard: React.FC<{ inboundOrder: InboundOrder }> = ({ inboundOrder }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const cancelMutation = useInboundBackorderCancelMutation();

  const backorders = inboundOrder.backorders ?? [];
  if (backorders.length === 0) return null;

  const handleClose = async (confirmed: boolean) => {
    setConfirmOpen(false);
    if (!confirmed) return;
    try {
      await cancelMutation.mutateAsync({
        params: { path: { inbound_order_id: inboundOrder.id! } },
      });
      // Cancel is async at the warehouse — the mutation only confirms the request
      // was accepted, not that the backorder is actually cancelled yet. Schedule a
      // delayed re-invalidation on top of the immediate one (inside the mutation's
      // onSuccess) to pick up the WMS confirmation/rejection without polling.
      toast.success(t('inbound.cancel_requested'));
      scheduleInboundRefetch(queryClient);
    } catch (e) {
      toast.error(t('inbound.backorder_cancel_error'));
    }
  };

  return (
    <>
      <div className="flex flex-col gap-y-16">
        <Divider className="" />
        <div className="flex items-center justify-between gap-x-24">
          <SectionHeadline>{t('inbound.backorders')}</SectionHeadline>
          <Button
            tone={ButtonToneProps.ERROR}
            title={t('inbound.backorder_cancel')}
            disabled={!inboundOrder.backorder_cancelable || cancelMutation.isPending}
            onClick={() => setConfirmOpen(true)}
          />
        </div>

        <div className="rounded-base border-subtle overflow-hidden">
          <table className="w-full border-collapse">
            <thead>
              <tr className="typo-text-12--bold text-faded">
                <th className="text-left p-12 w-[60px]">{t('inbound.backorder_no')}</th>
                <th className="text-left p-12">{t('inbound.backorder_product')}</th>
                <th className="text-left p-12 w-[160px]">{t('inbound.backorder_sku')}</th>
                <th className="text-left p-12 w-[120px]">{t('inbound.backorder_pending')}</th>
                <th className="text-left p-12 w-[200px]">{t('inbound.backorder_status')}</th>
              </tr>
            </thead>
            <tbody>
              {backorders.map((line, idx) => (
                <tr
                  key={`${line.product?.product_id ?? idx}-${line.status?.key ?? ''}`}
                  className="border-subtle-bottom typo-text-14">
                  <td className="p-12 typo-text-14--bold">{idx + 1}</td>
                  <td className="p-12 typo-text-14--bold">{line.product?.name}</td>
                  <td className="p-12">{line.product?.article_number}</td>
                  <td className="p-12">{line.pending_qty}</td>
                  <td className="p-12">
                    <Badge
                      className="max-w-min"
                      label={line.status?.label ?? ''}
                      expanded
                      colorOverride={line.status?.color ? line.status.color : ''}
                      type={line.status?.color ? '' : STATUS_BADGE[line.status?.key ?? ''] ?? BadgeType.Neutral}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmOpen}
        onClose={handleClose}
        title={t('inbound.backorder_cancel_confirm_title')}
        description={
          <>
            <span className="block">{t('inbound.backorder_cancel_confirm_desc')}</span>
            <span className="block mt-8">{t('inbound.cancel_requested_hint')}</span>
          </>
        }
      />
    </>
  );
};

export default InboundBackordersCard;
